import { useState, useEffect } from 'react'
import type { MagoMasterRequest, MagoMasterResponse } from '../types/master'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

export const useMagoMaster = () => {
  const [devices, setDevices] = useState<MagoMasterResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchDevices = async (destId?: number, status?: number) => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (destId !== undefined) params.append('dest_id', destId.toString())
      if (status !== undefined) params.append('status', status.toString())

      const url = `${API_BASE_URL}/api/mago${params.toString() ? `?${params.toString()}` : ''}`
      const response = await fetch(url, {
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch devices: ${response.statusText}`)
      }
      const data = await response.json()
      setDevices(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred')
    } finally {
      setLoading(false)
    }
  }

  const createDevice = async (data: MagoMasterRequest): Promise<MagoMasterResponse> => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE_URL}/api/mago`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || `Failed to create device: ${response.statusText}`)
      }

      const newDevice = await response.json()
      setDevices(prev => [...prev, newDevice])
      return newDevice
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateDevice = async (id: number, data: MagoMasterRequest): Promise<MagoMasterResponse> => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE_URL}/api/mago/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || `Failed to update device: ${response.statusText}`)
      }

      const updatedDevice = await response.json()
      setDevices(prev => prev.map(device => (device.id === id ? updatedDevice : device)))
      return updatedDevice
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const deleteDevice = async (id: number): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE_URL}/api/mago/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || `Failed to delete device: ${response.statusText}`)
      }

      setDevices(prev => prev.filter(device => device.id !== id))
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const getDeviceById = async (id: number): Promise<MagoMasterResponse> => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE_URL}/api/mago/${id}`, {
        credentials: 'include',
      })
      if (!response.ok) {
        throw new Error(`Failed to fetch device: ${response.statusText}`)
      }
      const data = await response.json()
      return data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let isMounted = true

    const loadDevices = async () => {
      try {
        setLoading(true)
        setError(null)

        console.log('Loading mago devices...')

        const response = await fetch(`${API_BASE_URL}/api/mago`, {
          credentials: 'include',
        })
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const data = await response.json()

        if (isMounted) {
          console.log('Mago devices loaded successfully:', data.length)
          setDevices(data)
        }
      } catch (err) {
        if (isMounted) {
          const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
          console.error('Error loading mago devices:', errorMessage)
          setError(errorMessage)
          setDevices([])
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadDevices()

    return () => {
      isMounted = false
    }
  }, [])

  return {
    devices,
    loading,
    error,
    refetch: fetchDevices,
    createDevice,
    updateDevice,
    deleteDevice,
    getDeviceById,
  }
}
